import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import LoginPage from '../pages/LoginPage'
import LandingPage from '../pages/LandingPage'
import TermsPage from '../pages/TermsPage'
import PrivacyPage from '../pages/PrivacyPage'
import SignupPage from '../pages/SignupPage'
import MagicLinkPage from '../pages/MagicLinkPage'
import ResetPasswordPage from '../pages/ResetPasswordPage'
import EmergencyRecoveryPage from '../pages/EmergencyRecoveryPage'
import AcceptInvitePage from '../pages/AcceptInvitePage'
import AcceptAgentInvitePage from '../pages/AcceptAgentInvitePage'
import ConfirmMasterPage from '../pages/ConfirmMasterPage'
import ShipperRoutes from './ShipperRoutes'
import FreightForwarderRoutes from './FreightForwarderRoutes'
import MasterRoutes from './MasterRoutes'
import FreightForwarderProfilePage from '../pages/shared/FreightForwarderProfilePage'

// Componente de rota protegida
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return <div>Carregando...</div>
  }

  if (!isAuthenticated) {
    // Usa query param para sobreviver a redirects externos (ex: Brevo email tracking)
    const redirectUrl = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?redirect=${redirectUrl}`} replace />
  }

  return <>{children}</>
}

// Componente para redirecionar cotações com ID - vai para detalhes
function QuoteIdRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/shipper/quotes/${id}`} replace />
}

// Componente para redirecionar cotações para edição
function QuoteEditRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/shipper/quotes/${id}/edit`} replace />
}

// Componente para redirecionar /auth/signup para /accept-invite
// Converte o parâmetro 'invite' para 'token' que a AcceptInvitePage espera
function AuthSignupRedirect() {
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const inviteToken = searchParams.get('invite')

  if (inviteToken) {
    return <Navigate to={`/accept-invite?token=${inviteToken}`} replace />
  }

  // Se não tem invite token, redireciona para signup normal
  return <Navigate to="/signup" replace />
}

// Componente de rota pública (apenas para não autenticados)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading, user } = useAuth()
  const location = useLocation()

  // Pega a URL de origem da query string
  const searchParams = new URLSearchParams(location.search)
  const redirectTo = searchParams.get('redirect')

  if (loading) {
    return <div>Carregando...</div>
  }

  if (isAuthenticated && user) {
    // Se tem uma URL de origem, redireciona para lá
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />
    }

    // Senão, redireciona baseado no tipo de empresa atual
    if (user.user_master) {
      return <Navigate to="/master/dashboard" replace />
    }
    if (user.current_company?.type === 'freight-forwarder') {
      return <Navigate to="/freight-forwarder/dashboard" replace />
    }
    return <Navigate to="/shipper/dashboard" replace />
  }

  return <>{children}</>
}

// Componente de redirecionamento baseado em perfil
function RoleBasedRedirect() {
  const { user } = useAuth()

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Redireciona baseado no tipo de empresa atual (lido diretamente do user para evitar race conditions)
  if (user.user_master) {
    return <Navigate to="/master/dashboard" replace />
  }

  if (user.current_company?.type === 'freight-forwarder') {
    return <Navigate to="/freight-forwarder/dashboard" replace />
  }

  return <Navigate to="/shipper/dashboard" replace />
}

export default function AppRoutes() {
  return (
    <Routes>
      {/* Rotas públicas */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignupPage />
          </PublicRoute>
        }
      />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/invite/accept/:token" element={<AcceptAgentInvitePage />} />
      <Route path="/auth/signup" element={<AuthSignupRedirect />} />
      <Route path="/auth/magic-link" element={<MagicLinkPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/emergency-recovery" element={<EmergencyRecoveryPage />} />
      <Route path="/confirm-master" element={<ConfirmMasterPage />} />

      {/* Rotas protegidas por perfil */}
      <Route
        path="/shipper/*"
        element={
          <ProtectedRoute>
            <ShipperRoutes />
          </ProtectedRoute>
        }
      />

      <Route
        path="/freight-forwarder/*"
        element={
          <ProtectedRoute>
            <FreightForwarderRoutes />
          </ProtectedRoute>
        }
      />

      <Route
        path="/master/*"
        element={
          <ProtectedRoute>
            <MasterRoutes />
          </ProtectedRoute>
        }
      />

      {/* Public FF profile (review showcase) */}
      <Route
        path="/freight-forwarders/:id"
        element={
          <ProtectedRoute>
            <FreightForwarderProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Redirects das rotas antigas para as novas */}
      <Route path="/dashboard" element={<RoleBasedRedirect />} />
      <Route path="/quotes" element={<Navigate to="/shipper/quotes" replace />} />
      <Route path="/quotes/new" element={<Navigate to="/shipper/quotes/new" replace />} />
      <Route path="/quotes/:id" element={<QuoteIdRedirect />} />
      <Route path="/quotes/:id/edit" element={<QuoteEditRedirect />} />
      <Route path="/dashboard/freight-forwarder" element={<Navigate to="/freight-forwarder/dashboard" replace />} />

      {/* Landing page pública */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/termos" element={<TermsPage />} />
      <Route path="/privacidade" element={<PrivacyPage />} />

      {/* 404 */}
      <Route path="*" element={<div>Página não encontrada</div>} />
    </Routes>
  )
}
